import type {
  Lens,
  LensPeriod,
  LensPeriodAnchor,
  LensPeriodComparison,
  LensPeriodDefault,
  LensPeriodWindow,
} from '~/types/leLens';

export type LensDateRangePreset = { id: string; label: string; anchor?: LensPeriodAnchor; window: LensPeriodWindow };

/** The windows a person can pick by name; each is the grammar value the server resolves. */
export const DATE_RANGE_PRESETS: ReadonlyArray<LensDateRangePreset> = [
  { id: 'yesterday', label: 'Yesterday', window: { days: 1 } },
  { id: 'todaySoFar', label: 'Today so far', anchor: 'now', window: { days: 1 } },
  { id: 'last7CompletedDays', label: 'Last 7 days', window: { days: 7 } },
  { id: 'last30CompletedDays', label: 'Last 30 days', window: { days: 30 } },
  { id: 'last90CompletedDays', label: 'Last 90 days', window: { days: 90 } },
  { id: 'weekToDate', label: 'Week to date', window: { toDate: 'week' } },
  { id: 'monthToDate', label: 'Month to date', window: { toDate: 'month' } },
  { id: 'yearToDate', label: 'Year to date', window: { toDate: 'year' } },
  { id: 'lastWeek', label: 'Last week', window: { calendar: 'week', back: 1 } },
  { id: 'lastMonth', label: 'Last month', window: { calendar: 'month', back: 1 } },
  { id: 'all', label: 'All time', window: 'all' },
];

export type LensCompareToPreset = {
  id: 'previous' | 'lastYear' | 'none';
  label: string;
  comparison: LensPeriodComparison | null;
};

export const COMPARE_TO_PRESETS: ReadonlyArray<LensCompareToPreset> = [
  { id: 'previous', label: 'Previous period', comparison: 'previous' },
  { id: 'lastYear', label: 'Same period last year', comparison: { back: 1, unit: 'year' } },
  { id: 'none', label: 'None', comparison: null },
];

/** The server's own opening window when a board states none. */
export const LENS_DEFAULT_PERIOD: LensPeriod = { anchor: 'yesterday', window: { days: 90 }, comparison: null };

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function dateRangePresetById(id: string): LensDateRangePreset | undefined {
  return DATE_RANGE_PRESETS.find((preset) => preset.id === id);
}

export function matchingDateRangePreset(
  period: Pick<LensPeriod, 'anchor' | 'window'>,
): LensDateRangePreset | undefined {
  return DATE_RANGE_PRESETS.find(
    (preset) => same(preset.window, period.window) && (preset.anchor ?? 'yesterday') === (period.anchor ?? 'yesterday'),
  );
}

export function matchingCompareTo(
  comparison: LensPeriodComparison | null | undefined,
): LensCompareToPreset | undefined {
  return COMPARE_TO_PRESETS.find((preset) => same(preset.comparison, comparison ?? null));
}

/** The saved opening period however it was written: the new shape, else the old one read as it. */
export function periodFromLegacyDefault(value: LensPeriodDefault | undefined): LensPeriod | undefined {
  if (!value) {
    return undefined;
  }
  if (value.type === 'lastDays') {
    return { window: { days: value.days } };
  }
  if (value.type === 'yearToDate') {
    return { window: { toDate: 'year' } };
  }
  if (value.type === 'monthToDate') {
    return { window: { toDate: 'month' } };
  }
  return { window: { from: value.from, to: value.to } };
}

export function boardPeriodOf(spec: Pick<Lens['spec'], 'period' | 'periodDefault'>): LensPeriod {
  return { ...LENS_DEFAULT_PERIOD, ...(spec.period ?? periodFromLegacyDefault(spec.periodDefault)) };
}

/** Later layers win per key, the way the server merges a card's pin over the board. */
export function mergePeriods(...layers: Array<Partial<LensPeriod> | undefined>): LensPeriod {
  const merged: LensPeriod = { ...LENS_DEFAULT_PERIOD };
  for (const layer of layers) {
    if (!layer) {
      continue;
    }
    if (layer.window !== undefined) {
      merged.window = layer.window;
    }
    if (layer.anchor !== undefined) {
      merged.anchor = layer.anchor;
    }
    if (layer.comparison !== undefined) {
      merged.comparison = layer.comparison;
    }
  }
  return merged;
}

/** The words for a window: its preset name, the two days of a fixed one, else the grammar itself. */
export function periodLabel(period: Pick<LensPeriod, 'anchor' | 'window'>): string {
  const preset = matchingDateRangePreset(period);
  if (preset) {
    return preset.label;
  }
  if (typeof period.window === 'object' && 'from' in period.window) {
    return `${period.window.from} to ${period.window.to}`;
  }
  return JSON.stringify(period.window);
}

export function isLensPeriod(value: unknown): value is Partial<LensPeriod> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    ('window' in value || 'anchor' in value || 'comparison' in value)
  );
}
